import DOMPurify from "dompurify";
import parse, {
  DOMNode,
  Element,
  HTMLReactParserOptions,
} from "html-react-parser";
import React, { ReactElement } from "react";

// Initialize DOMPurify only on client side
const purify = typeof window !== "undefined" ? DOMPurify : null;

// Define allowed protocols as constants
const ALLOWED_PROTOCOLS = ["https:", "mailto:", "tel:"];

// Update isValidUrl to handle email and phone protocols
const isValidUrl = (url: string): boolean => {
  try {
    // Handle mailto: and tel: protocols separately
    if (url.startsWith("mailto:")) {
      const email = url.substring(7);
      // Basic email validation
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }
    if (url.startsWith("tel:")) {
      const phone = url.substring(4);
      // Basic phone number validation (allows digits, spaces, +, -, and parentheses)
      return /^[\d\s+\-()]+$/.test(phone);
    }

    const parsed = new URL(url);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch (error: unknown) {
    console.error(error);
    return false;
  }
};

// Helper function to handle tag replacement
const replaceNode = (domNode: DOMNode): ReactElement | null => {
  if ((domNode as Element).name) {
    const element = domNode as Element;
    const { name, attribs } = element;

    if (name === "a" && attribs?.href) {
      if (!isValidUrl(attribs.href)) {
        return <span>[Ogiltig Länk]</span>;
      }
      // Säkerställ säkra länkmål
      attribs.rel = "noopener noreferrer";
      attribs.target = "_blank";
    }

    if (name === "img" && attribs?.src) {
      if (!isValidUrl(attribs.src)) {
        return <span>[Ogiltig Bild]</span>;
      }
    }

    return null;
  }

  // Handle text nodes to replace standalone 'kr' with 'SEK'
  if (domNode.type === "text") {
    const text = domNode.data;
    const regex = /\bkr\b/g; // Matches 'kr' as a whole word

    if (regex.test(text)) {
      const parts = text.split(regex);
      const matches = text.match(regex);

      return (
        <>
          {parts.map((part, index) => (
            <React.Fragment key={index}>
              {part}
              {matches && matches[index] ? <span>SEK</span> : null}
            </React.Fragment>
          ))}
        </>
      );
    }
  }

  return null;
};

export function handleParse(content: string): React.ReactElement {
  // Only sanitize on client side
  const sanitized = purify ? purify.sanitize(content) : content;

  const options: HTMLReactParserOptions = {
    replace: replaceNode,
  };

  return <div className="parsed-content">{parse(sanitized, options)}</div>;
}
